import React, { useState } from 'react';

// Product Legend Component
interface ProductLegendProps {
    products: string[];
    visibleProducts: Record<string, boolean>;
    onToggleProduct: (product: string) => void;
    generateColor: (index: number) => string;
    chartData?: any; // Add chart data to calculate sums
    divisor: number; //allows things like minutes to be displayed as hours
    valuePostfix: string;
    labelSumDecimalPlaces?: number;
    isMobile?: boolean; // Add mobile prop for collapsible behavior
    title?: string;
}

const ProductLegend: React.FC<ProductLegendProps> = ({
    products,
    visibleProducts,
    onToggleProduct,
    generateColor,
    chartData,
    divisor = 1,
    valuePostfix,
    labelSumDecimalPlaces = 2,
    isMobile = false,
    title = 'Products'
}) => {
    const [sortMode, setSortMode] = useState<'alphabetical' | 'value'>('value');
    const [isExpanded, setIsExpanded] = useState(false);

    // Calculate sum of values for each product
    const getProductSum = (product: string): number => {
        if (!chartData?.datasets) return 0;

        const productDataset = chartData.datasets.find((dataset: any) => dataset.label === product);
        if (!productDataset?.data) return 0;

        let value = productDataset.data.reduce((sum: number, value: number) => sum + (value || 0), 0);
        //fix to 2 decimal places
        value = (value / divisor).toFixed(labelSumDecimalPlaces);
        return value;
    };

    // Get sorted products based on current sort mode
    const getSortedProducts = (): string[] => {
        const productsWithSums = products.map(product => ({
            name: product,
            sum: getProductSum(product)
        }));

        if (sortMode === 'alphabetical') {
            return productsWithSums
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(item => item.name);
        } else {
            return productsWithSums
                .sort((a, b) => b.sum - a.sum) // Descending order (highest first)
                .map(item => item.name);
        }
    };

    const sortedProducts = getSortedProducts();

    // Count active products
    const activeCount = Object.values(visibleProducts).filter(Boolean).length;
    const totalCount = products.length;

    // Mobile collapsed view
    if (isMobile && !isExpanded) {
        return (
            <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '0.75rem'
            }}>
                <button
                    onClick={() => setIsExpanded(true)}
                    style={{
                        background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 12h18m-9-9v18" />
                    </svg>
                    Filter ({activeCount}/{totalCount})
                </button>
            </div>
        );
    }

    return (
        <div style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '1rem',
            maxHeight: isMobile ? 'none' : '400px'
        }}>
            {/* Header with sort buttons */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
            }}>
                {isMobile && (
                    <button
                        onClick={() => setIsExpanded(false)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            color: '#6c757d',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        title="Collapse filter"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                )}
                <h3 style={{
                    margin: 0,
                    fontSize: '1rem',
                    color: '#333',
                    fontWeight: '600'
                }}>
                    {title}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        onClick={() => setSortMode('alphabetical')}
                        style={{
                            background: sortMode === 'alphabetical' ? '#007bff' : '#e9ecef',
                            color: sortMode === 'alphabetical' ? 'white' : '#6c757d',
                            border: 'none',
                            padding: '0.25rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '28px',
                            height: '28px'
                        }}
                        title="Sort alphabetically"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18" />
                            <path d="M7 12h10" />
                            <path d="M10 18h4" />
                        </svg>
                    </button>
                    <button
                        onClick={() => setSortMode('value')}
                        style={{
                            background: sortMode === 'value' ? '#007bff' : '#e9ecef',
                            color: sortMode === 'value' ? 'white' : '#6c757d',
                            border: 'none',
                            padding: '0.25rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '28px',
                            height: '28px'
                        }}
                        title="Sort by value"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 3v18h18" />
                            <path d="M7 12l4-4 4 4 4-4" />
                        </svg>
                    </button>
                </div>
            </div>
            {/* List of products */}
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '0.5rem', 
                overflowY: isMobile ? 'visible' : 'scroll', 
                height: isMobile ? 'auto' : '300px',
                maxHeight: isMobile ? '60vh' : '300px'
            }}>
                {sortedProducts.map((product) => {
                    const originalIndex = products.indexOf(product);
                    const sum = getProductSum(product);

                    return (
                        <label
                            key={product}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                cursor: 'pointer',
                                padding: '0.25rem',
                                borderRadius: '4px',
                                transition: 'background-color 0.2s',
                                fontSize: '0.875rem'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#e9ecef';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={visibleProducts[product] || false}
                                onChange={() => onToggleProduct(product)}
                                style={{ margin: 0 }}
                            />
                            <div
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: generateColor(originalIndex),
                                    borderRadius: '2px',
                                    flexShrink: 0
                                }}
                            />
                            <span style={{
                                color: visibleProducts[product] ? '#333' : '#999',
                                fontWeight: visibleProducts[product] ? '500' : '400',
                                flex: 1
                            }}>
                                {product} ({sum.toLocaleString()}{valuePostfix})
                            </span>
                        </label>
                    );
                })}
            </div>
            {/* Show all and hide all buttons */}
            <div style={{
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #dee2e6',
                display: 'flex',
                gap: '0.5rem'
            }}>
                <button
                    onClick={() => {
                        products.forEach(product => {
                            if (!visibleProducts[product]) {
                                onToggleProduct(product);
                            }
                        });
                    }}
                    style={{
                        background: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                    }}
                >
                    Show All
                </button>
                <button
                    onClick={() => {
                        products.forEach(product => {
                            if (visibleProducts[product]) {
                                onToggleProduct(product);
                            }
                        });
                    }}
                    style={{
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                    }}
                >
                    Hide All
                </button>
            </div>
        </div>
    );
};

export default ProductLegend; 